import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import {
  combineLatest,
  filter,
  finalize,
  forkJoin,
  iif,
  of,
  switchMap,
  takeUntil,
} from "rxjs";
import { ToastrService } from "ngx-toastr";
import { TranslateService } from "@ngx-translate/core";
import { MatDialog } from "@angular/material/dialog";
import { BaseComponent } from "../../../base.component";
import { ExecuteVisitType } from "../../../models/execute-visit.model";
import { ICriterion } from "../../../../distribute-team-members/models/criterion";
import { DecisionInput } from "../../../models/decision-input.model";
import { Category } from "../../../models/category.model";
import { CriterionsService } from "../../../services/criterions.service";
import { CriterionInformation } from "../../../models/criterion-information.mode";
import { showToast } from "../../../../toast.helper";
import { CopyModel } from "../../../models/copy.model";
import { MasarCriterionsService } from "../../../services/masar-criterions.service";

@Component({
  selector: "app-masar-criteria-decision",
  templateUrl: "./masar-criteria-decision.component.html",
  styleUrls: ["./masar-criteria-decision.component.scss"],
})
export class MasarCriteriaDecisionComponent
  extends BaseComponent
  implements OnInit, OnChanges
{
  @Input() memberId: string;
  @Input() decisionPreview: boolean;
  isLoadingResults: boolean;
  form: FormGroup;
  isMain = false;
  criterion: ICriterion;
  decisionInput: DecisionInput;
  modelId: number;
  mistakeId: number;
  criterion$ = this.criterionsService.criterion$;

  category: Category;
  get isFinalReport() {
    return (
      !this.decisionPreview &&
      (this.decisionInput.executeVisitType == ExecuteVisitType.FinalReport ||
        this.decisionInput.executeVisitType ==
          ExecuteVisitType.InstitutionEntries ||
        this.decisionInput.executeVisitType ==
          ExecuteVisitType.FinalReportWithInstitutionEntries)
    );
  }

  get showInstitutionEntries() {
    return (
      this.decisionInput.executeVisitType ==
        ExecuteVisitType.InstitutionEntries ||
      this.decisionInput.executeVisitType ==
        ExecuteVisitType.FinalReportWithInstitutionEntries
    );
  }

  get disabledInstitutionEntries() {
    return (
      this.decisionInput.viewOnly ||
      this.decisionInput.executeVisitType ==
        ExecuteVisitType.FinalReportWithInstitutionEntries
    );
  }

  get isInstitutionEntries() {
    return (
      this.decisionInput.executeVisitType == ExecuteVisitType.InstitutionEntries
    );
  }

  get isFinalReportWithInstitutionEntries() {
    return (
      this.decisionInput.executeVisitType ==
      ExecuteVisitType.FinalReportWithInstitutionEntries
    );
  }
  get enabledCopy() {
    return !!this.memberId;
  }
  get enabledCopyToExecutiveSummary() {
    return (
      this.masarCriterionsService.allCriterionEvaluated$.value &&
      this.decisionInput.enableEdit &&
      this.decisionInput.executeVisitType == ExecuteVisitType.FinalReport
    );
  }
  get isFormDisabled() {
    return (
      !this.decisionInput.enableEdit ||
      this.enabledCopy ||
      this.isInstitutionEntries ||
      this.isFinalReportWithInstitutionEntries ||
      !this.criterion$.value.allQuestionsAnswered ||
      !this.category?.allSubsEvaluated ||
      !!this.isSessionNotStarted
    );
  }
  get isSessionNotStarted() {
    return (
      this.decisionInput?.visitMetaData?.sessionChecked &&
      !this.decisionInput?.visitMetaData?.sessionChecked?.sessionStarted
    );
  }

  constructor(
    private formBuilder: FormBuilder,
    private criterionsService: CriterionsService,
    private masarCriterionsService: MasarCriterionsService,
    private toastrService: ToastrService,
    private translateService: TranslateService,
    private matDialog: MatDialog
  ) {
    super();
  }

  ngOnInit() {
    combineLatest([
      this.criterionsService.criterion$,
      this.criterionsService.decisionInputs$,
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([criterion, inputs]) => {
        this.criterion = criterion;
        this.decisionInput = inputs;
        this.initForm();
        this.getData();
        this.registerCopyEvaluation();
      });
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.memberId?.previousValue &&
      changes.memberId?.previousValue != changes.memberId?.currentValue
    ) {
      this.getData();
    }
  }

  copy(controlName: string) {
    const value = this.form.controls[controlName].value;
    if (this.enabledCopyToExecutiveSummary)
      this.criterionsService.sendToExecutiveSummary$.next(
        new CopyModel(controlName, value)
      );
    else
      this.criterionsService.sendEvaluation$.next(
        new CopyModel(controlName, value)
      );
  }

  submit() {
    if (this.form.valid) {
      this.isLoadingResults = true;
      const value = this.form.value;

      const data: CriterionInformation = {
        id: this.modelId,
        criterionId: this.criterion.id,
        criterionKey: this.criterion.criterionKey,
        visitKey: this.decisionInput.visitMetaData.visitKey,
        memberId: this.decisionInput.visitMetaData.memberId,
        ...value,
      };

      of(!!this.modelId)
        .pipe(
          switchMap((isUpdate) =>
            iif(
              () => isUpdate,
              this.masarCriterionsService.editCriterionInformation(
                data,
                this.isFinalReport
              ),
              this.masarCriterionsService.createCriterionInformation(
                data,
                this.isFinalReport
              )
            )
          ),
          finalize(() => (this.isLoadingResults = false))
        )
        .subscribe((model) => {
          this._updateForm(model);
          this.criterionsService.decisionTaken$.next();
          this.masarCriterionsService.updatedMainCriterion$.next(model);
          showToast(
            this.toastrService,
            this.translateService.instant("forms.common.Criteria.Saved done"),
            this.translateService.instant(
              "forms.common.Criteria.Saved Successfully"
            ),
            false
          );
        });
    }
  }
  private initCategory(info: CriterionInformation) {
    this.category = {
      totalEvaluationCriteria: info?.totalEvaluationCriteria || 0,
      numberOfApplicable: info?.numberOfApplicable || 0,
      calculatedSavedScore: info?.numberOfApplicable
        ? info?.totalEvaluationCriteria / info?.numberOfApplicable
        : 0,
      allSubsEvaluated: info.allSubsEvaluated,
    };
  }

  private initForm() {
    this.isMain = this.criterion.criterionType == "MAIN";
    this.form = this.formBuilder.group({
      criterionSummary: [
        { value: "", disabled: this.isFormDisabled },
        [Validators.required],
      ],
      complianceEvaluation: [
        { value: "", disabled: this.isFormDisabled },
        [Validators.required],
      ],
      summaryOfRecommendations: [
        { value: "", disabled: this.isFormDisabled },
        [Validators.required],
      ],
      summaryOfCommendations: [
        { value: "", disabled: this.isFormDisabled },
        [Validators.required],
      ],
      summaryOfSuggestions: [
        { value: "", disabled: this.isFormDisabled },
        [Validators.required],
      ],
    });
  }

  private getData() {
    this.isLoadingResults = true;
    this.masarCriterionsService
      .getCriterionInformationForMainCriterion(
        this.decisionInput.visitMetaData.visitKey,
        this.criterion.criterionKey,
        this.memberId || this.decisionInput.currentMemberId,
        this.isFinalReport
      )
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isLoadingResults = false))
      )
      .subscribe((criterionInformation) => {
        this.initCategory(criterionInformation);
        this.initForm();
        this._updateForm(criterionInformation);
      });
  }
  private _updateForm(data: CriterionInformation) {
    this.modelId = data?.id;
    this.form.patchValue({
      criterionSummary: data?.criterionSummary,
      complianceEvaluation: data?.complianceEvaluation,
      summaryOfRecommendations: data?.summaryOfRecommendations,
      summaryOfCommendations: data?.summaryOfCommendations,
      summaryOfSuggestions: data?.summaryOfSuggestions,
    });
  }
  private registerCopyEvaluation() {
    if (!this.enabledCopy)
      this.criterionsService.sendEvaluation$
        .pipe(
          filter((data) => !!data),
          takeUntil(this.destroy$)
        )
        .subscribe((data) => {
          this.form.patchValue({
            [data.key]: data.value,
          });
        });
  }
}
